<template>
  <v-container class="settings-container">
    <!-- Snackbar for notifications -->
    <v-snackbar v-if="snackbar.show && snackbar?.text?.length > 0" v-model="snackbar.show" :color="snackbar?.error ? 'error' : 'success'" :timeout="3000" rounded="lg"
      location="top">
      <p class="text-center font-weight-bold mb-0 text-white">{{ snackbar?.text }}</p>
    </v-snackbar>

    <!-- Page Header -->
    <v-row align="center" justify="space-between" class="mb-4">
      <v-col :cols="$vuetify.display.smAndDown ? 12 : 'auto'">
        <h1 class="page-title mb-1">Forms</h1>
        <p class="body-text text-medium-emphasis mt-2">
          Create, manage, and track all your forms in one place.
        </p>
      </v-col>
      <v-col :cols="$vuetify.display.smAndDown ? 12 : 'auto'" class="text-right">
        <v-breadcrumbs :items="breadcrumbs" class="pa-0">
          <template v-slot:divider>
            <v-icon>mdi-chevron-right</v-icon>
          </template>
        </v-breadcrumbs>
      </v-col>
    </v-row>

    <!-- Account Status Alerts -->
    <v-alert v-if="!canCreateForm" variant="tonal" icon="mdi-alert-circle" class="mb-6" color="accent-color"
      rounded="lg" density="compact">
      <span v-if="userMixin_hasActiveFreePlan || (userMixin_hasActiveFreeTrialPlan && userMixin_accountExpired)">
        Your account has expired. Please <router-link :to="{ name: 'plans.index' }"
          class="text-accent-color font-weight-bold">upgrade your account</router-link> to continue.
      </span>
      <span v-else-if="userMixin_hasActiveFreeTrialPlan && !userMixin_inTrial">
        Your trial has ended. Please <router-link :to="{ name: 'plans.index' }"
          class="text-accent-color font-weight-bold">upgrade your
          account</router-link> to continue.
      </span>
      <span v-else-if="userMixin_isPastDueUser">
        The billing on your account failed. Please <router-link :to="{ name: 'plans.index' }"
          class="text-accent-color font-weight-bold">update your payment details</router-link> to reactivate your
        account.
      </span>
      <span v-else-if="userMixin_hasPausedSubscription">
        You do not have an active subscription. Please <router-link :to="{ name: 'plans.index' }"
          class="text-accent-color font-weight-bold">reactivate your account</router-link> to continue.
      </span>
      <span v-else-if="!hasFormsFeatureAccess">
        You don't have access to forms. Please <router-link :to="{ name: 'plans.index' }"
          class="text-accent-color font-weight-bold">Click here</router-link> to upgrade your account.
      </span>
      <span v-else>
        Your form limit has been reached. Please <router-link :to="{ name: 'plans.index' }"
          class="text-accent-color font-weight-bold">upgrade
          your plan</router-link> to create more forms.
      </span>
    </v-alert>

    <!-- Search and Action Buttons -->
    <v-row align="center" justify="space-between" class="mb-6">
      <v-col :cols="$vuetify.display.smAndDown ? 12 : 6">
        <v-text-field v-if="listData?.selected?.length === 0" v-model="pagination.search" density="compact"
          label="Search forms by title" clearable prepend-inner-icon="mdi-magnify" variant="outlined" hide-details
          max-width="500px" />
        <h2 v-else class="section-title">
          {{ listData.selected.length }} Form{{ listData.selected.length > 1 ? 's' : '' }}
          Selected
        </h2>
      </v-col>
      <v-col :cols="$vuetify.display.smAndDown ? 12 : 'auto'" class="text-right d-flex flex-wrap justify-end ga-2">
        <!-- Bulk Action Buttons -->
        <template v-if="listData?.selected?.length > 0">
          <v-btn v-if="bulkDuplicateAllowed" prepend-icon="mdi-content-copy" color="info"
            :block="$vuetify.display.smAndDown" @click="openActionModal('duplicate')">
            Duplicate ({{ listData.selected.length }})
          </v-btn>
          <v-btn prepend-icon="mdi-archive" color="danger-color" :block="$vuetify.display.smAndDown"
            @click="openActionModal('archive')">
            Archive ({{ listData.selected.length }})
          </v-btn>
        </template>
        <!-- Regular Action Buttons -->
        <template v-else>
          <v-btn v-if="canCreateForm && listData.forms.length >= 1" prepend-icon="mdi-plus" color="brand-color"
            :block="$vuetify.display.smAndDown" @click="createFormDialog = true">
            Create Form
          </v-btn>
          <v-btn prepend-icon="mdi-archive-cog" color="danger-color" variant="outlined"
            :block="$vuetify.display.smAndDown" @click="$router.push({ name: 'forms.archive.list' })">
            Archived Forms
          </v-btn>
          <v-btn v-if="isAdmin" prepend-icon="mdi-share-variant" color="info" variant="outlined"
            :block="$vuetify.display.smAndDown" @click="$router.push({ name: 'form.share' })">
            Share
          </v-btn>
        </template>
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-data-table-server v-model="listData.selected" show-select selection-color="success" :headers="headers"
      :items="listData.forms" :items-length="pagination.totalItems" :loading="listData.loading"
      :items-per-page="pagination.itemsPerPage" :items-per-page-options="pagination.perPageOptions"
      v-model:page="pagination.currentPage" v-model:items-per-page="pagination.itemsPerPage" @update:options="loadItems"
      class="elevation-0 data-table-modern rounded-lg">

      <template v-slot:loading>
        <v-skeleton-loader class="mx-auto" max-width="100%" type="table-row@3"></v-skeleton-loader>
      </template>
      <template v-slot:loader>
      </template>

      <template v-slot:no-data>
        <div v-if="listData.error" class="text-center py-8">
          <v-icon size="64" color="error" class="mb-4">mdi-alert-circle</v-icon>
          <h3 class="text-h6 text-error mb-2">Error Loading Forms</h3>
          <p class="text-body-2 text-grey-darken-1 mb-4">
            {{ listData.message || 'Failed to load forms. Please try again.' }}
          </p>
          <v-btn color="error" variant="outlined" @click="loadItems({})">
            <v-icon left>mdi-refresh</v-icon>
            Retry
          </v-btn>
        </div>
        <div v-else class="text-center py-8">
          <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-form-select</v-icon>
          <h3 class="text-h6 text-grey-darken-1 mb-2">No Forms Found</h3>
          <p class="text-body-2 text-grey-darken-1 mb-4">
            {{ pagination.search ? 'No forms match your search criteria.' : 'Get started by creating your first form.'
            }}
          </p>
          <v-btn v-if="canCreateForm && !pagination.search" color="brand-color" prepend-icon="mdi-plus"
            @click="createFormDialog = true">
            Create Form
          </v-btn>
        </div>
      </template>

      <template v-if="leadNotificationSetting?.enabled" v-slot:[`header.notifications`]>
        <v-badge class="cursor-pointer" @click="handleNotificationClick"
          :content="totalUnreadLeads > 99 ? '99+' : totalUnreadLeads"
          :color="totalUnreadLeads === 0 ? 'grey' : 'accent-color'">
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
              <div v-bind="props">
                <v-icon v-if="!notification.loader" icon="mdi-bell" size="default" @click="handleNotificationClick"></v-icon>
                <v-progress-circular v-else indeterminate size="24" width="2" color="grey"></v-progress-circular>
              </div>
            </template>
            <span>{{ totalUnreadLeads === 0 ? 'No new leads' : `Click to mark ${totalUnreadLeads} new
              lead${totalUnreadLeads > 1 ?
                's' : ''} as read` }}</span>
          </v-tooltip>
        </v-badge>
      </template>

      <template v-slot:[`item.serial_no`]="{ item }">
        {{ ((pagination.currentPage - 1) * pagination.itemsPerPage) + listData.forms.indexOf(item) + 1 }}
      </template>

      <template v-slot:[`item.title`]="{ item }">
        <div class="py-4 cursor-not-allowed">
          <span v-if="item.blocked" class="py-4 text-accent-color">{{ item.title }}</span>
          <router-link class="text-brand-color" :to="{ name: 'forms.overview', params: { id: item.id } }" v-else>
            {{ item.title }}
          </router-link>
        </div>
      </template>

      <template v-slot:[`item.conversion_rate`]="{ item }">
        {{ item.conversion_rate }}%
      </template>

      <template v-slot:[`item.form_total_leads_count`]="{ item }">
        <router-link class="text-accent-color" :to="{ name: 'forms.leads', params: { id: item.id } }">
          <v-chip color="info" variant="tonal">{{ item.form_total_leads_count || 0 }}</v-chip>
        </router-link>
      </template>

      <template v-slot:[`item.form_leads_count`]="{ item }">
        <v-chip color="success" variant="tonal">{{ item.form_leads_count || 0 }}</v-chip>
      </template>

      <template v-slot:[`item.form_leads_partial_count`]="{ item }">
        <v-chip color="accent-color" variant="tonal">{{ item.form_leads_partial_count || 0 }}</v-chip>
      </template>

      <template v-if="leadNotificationSetting?.enabled" v-slot:[`item.notifications`]="{ item }">
        <v-badge :content="item.unread_leads > 99 ? '99+' : (item.unread_leads || 0)"
          :color="!item.unread_leads ? 'grey' : 'accent-color'" inline></v-badge>
      </template>

      <template v-slot:[`item.created_at`]="{ item }">
        {{ localeDate(item.created_at) }}
      </template>

      <!-- <template v-slot:[`item.updated_at`]="{ item }">
        {{ localeDate(item.updated_at) }}
      </template> -->

      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex justify-center">
          <v-speed-dial v-if="!item.blocked" v-model="item.speedDial" location="left center">
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" color="grey-lighten-3" icon size="x-small" class="elevation-0">
                <v-icon>{{ item.speedDial ? 'mdi-close' : 'mdi-dots-vertical' }}</v-icon>
              </v-btn>
            </template>

            <a :key="`preview-${item.id}`" :href="formUrl(item.key)" target="_blank" class="text-decoration-none">
              <v-btn color="brand-color" icon size="x-small" variant="flat">
                <v-icon>mdi-open-in-new</v-icon>
                <v-tooltip activator="parent" location="top">
                  <span>Preview Form</span>
                </v-tooltip>
              </v-btn>
            </a>

            <a :key="`duplicate-${item.id}`" v-if="bulkDuplicateAllowed" href="#"
              @click.prevent="openActionModal('duplicate', item)" class="text-decoration-none">
              <v-btn color="info" icon size="x-small" variant="flat">
                <v-icon>mdi-content-copy</v-icon>
                <v-tooltip activator="parent" location="top">
                  <span>Duplicate Form</span>
                </v-tooltip>
              </v-btn>
            </a>

            <a :key="`archive-${item.id}`" href="#" @click.prevent="openActionModal('archive', item)"
              class="text-decoration-none">
              <v-btn color="danger-color" icon size="x-small" variant="flat">
                <v-icon>mdi-archive</v-icon>
                <v-tooltip activator="parent" location="top">
                  <span>Archive Form</span>
                </v-tooltip>
              </v-btn>
            </a>
          </v-speed-dial>

          <!-- Blocked form state remains outside speed dial -->
          <v-tooltip v-if="item.blocked_reason === 'phishing_high_risk'" location="top" max-width="320">
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" color="accent-color" rounded="lg" size="small" prepend-icon="mdi-shield-alert" class="text-none" variant="tonal">
                Blocked For Review
              </v-btn>
            </template>
            <span>{{ item.blocked_tooltip }}</span>
          </v-tooltip>
          <router-link v-else-if="item.blocked" :to="{ name: 'plans.index' }" class="text-decoration-none">
            <v-btn color="accent-color" rounded="lg" size="small" prepend-icon="mdi-open-in-new" class="text-none text-white">
              Upgrade To Access
            </v-btn>
          </router-link>
        </div>
      </template>
    </v-data-table-server>

    <!-- Action Confirmation Dialog -->
    <v-dialog v-model="model.isOpenModal" max-width="550" persistent>
      <v-card>
        <div class="card-section-header">
          <div class="d-flex align-center ga-3">
            <v-avatar :color="getActionColor(model.action)" variant="tonal" size="36">
              <v-icon :color="getActionColor(model.action)">mdi-alert-circle</v-icon>
            </v-avatar>
            <h2 class="section-title">Confirm {{ getActionTitle(model.action) }}</h2>
          </div>
        </div>
        <v-card-text class="pa-6">
          <v-alert :type="getAlertType(model.action)" variant="tonal" :icon="getActionIcon(model.action)">
            <p v-if="model.form" class="mb-0">
              You are about to {{ model.action }} the form <strong>{{ model.form.title }}</strong>. Are you sure you
              want to
              proceed?
            </p>
            <p v-else class="mb-0">
              Are you sure you want to {{ model.action }} <strong>{{ listData.selected.length }}</strong>
              selected {{ listData.selected.length > 1 ? 'forms' : 'form' }}?
            </p>
          </v-alert>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer />
          <v-btn color="grey-darken-1" variant="outlined" @click="model.isOpenModal = false">
            Cancel
          </v-btn>
          <v-btn :color="getActionColor(model.action)" variant="flat" :loading="model.loader"
            @click="confirmAction(model.action)">
            {{ getActionTitle(model.action) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create Form Dialog -->
    <v-dialog v-model="createFormDialog" max-width="900" persistent>
      <v-card rounded="lg" elevation="0">
        <div class="card-section-header">
          <div class="d-flex align-center justify-space-between flex-wrap ga-3">
            <div class="d-flex align-center ga-3">
              <v-avatar color="brand-color" variant="tonal" size="36">
                <v-icon color="brand-color">mdi-form-select</v-icon>
              </v-avatar>
              <div>
                <h2 class="section-title">Start a new form</h2>
                <p class="text-body-2 text-medium-emphasis mb-0">
                  Create an amazing form to easily collect leads and data
                </p>
              </div>
            </div>
            <v-btn icon="mdi-close" variant="text" size="small" color="grey-darken-1"
              @click="createFormDialog = false" />
          </div>
        </div>

        <v-card-text class="pa-6 pt-2">
          <v-row>
            <v-col cols="12" md="6">
              <v-card class="form-option-card" elevation="0" rounded="lg" @click="navigateToBlank">
                <!-- Visual Preview Section with padding and border radius -->
                <div class="form-option-preview-wrapper">
                  <div class="form-option-preview">
                    <div class="preview-content">
                      <!-- Blank form visual representation -->
                      <div class="blank-form-visual">
                        <div class="form-placeholder-line short"></div>
                        <div class="form-placeholder-line"></div>
                        <div class="form-placeholder-line"></div>
                        <div class="form-placeholder-line medium"></div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Content Section -->
                <v-card-text class="pa-6">
                  <h3 class="text-h6 font-weight-bold mb-2">Blank</h3>
                  <p class="text-body-2 text-medium-emphasis mb-0">
                    Start from scratch and build your custom form with complete flexibility
                  </p>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card class="form-option-card" elevation="0" rounded="lg" @click="navigateToTemplate">
                <!-- Visual Preview Section with padding and border radius -->
                <div class="form-option-preview-wrapper">
                  <div class="form-option-preview">
                    <div class="preview-content">
                      <!-- Template visual representation - multiple form cards -->
                      <div class="template-visual">
                        <div class="template-card">
                          <div class="template-card-header"></div>
                          <div class="template-card-line"></div>
                          <div class="template-card-line short"></div>
                        </div>
                        <div class="template-card offset">
                          <div class="template-card-header"></div>
                          <div class="template-card-line"></div>
                          <div class="template-card-line short"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Content Section -->
                <v-card-text class="pa-6">
                  <h3 class="text-h6 font-weight-bold mb-2">Use Template</h3>
                  <p class="text-body-2 text-medium-emphasis mb-0">
                    Choose from pre-designed templates to get started quickly and efficiently
                  </p>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import helperMixin from '@/mixins/helper'
import leadMixin from '@/mixins/lead.js'
import userMixin from '@/mixins/user'
import packageBuilderMixin from '@/mixins/package-builder'
import { mapGetters } from 'vuex'
import { resourceTypes } from '@/shared/constants/generalList/list'
import config from '@/api/config'
import * as actionTypes from '@/store/action-types'
import { featureSlugs } from '@/shared/constants/packagebuilder/features'

export default {
  mixins: [helperMixin, userMixin, packageBuilderMixin, leadMixin],
  data: function () {
    return {
      breadcrumbs: [
        {
          title: 'Dashboard',
          disabled: false,
          to: { name: 'dashboard' }
        },
        {
          title: 'Forms',
          disabled: true
        }
      ],
      pagination: {
        search: '',
        currentPage: 1,
        itemsPerPage: 15,
        perPageOptions: [15, 25, 50, 100],
        totalItems: 0
      },
      listData: {
        forms: [],
        loading: false,
        error: false,
        selected: [],
        message: null
      },
      model: {
        isOpenModal: false,
        loader: false,
        message: null,
        form: null,
        action: null // 'archive' or 'duplicate'
      },
      notification: {
        loader: false,
        viewed: false
      },
      snackbar: {
        text: '',
        error: false,
        show: false,
        timeout: 5000
      },
      createFormDialog: false,
    }
  },
  computed: {
    ...mapGetters([
      'forms',
      'token',
      'user'
    ]),
    formLimit() {
      return this.pBMixin_getFormLimit()
    },

    bulkDuplicateAllowed() {
      return this.hasFormsFeatureAccess && this.pBMixin_hasFormLimit(this.pagination.totalItems)
    },

    canCreateForm() {
      return this.hasFormsFeatureAccess && this.pBMixin_hasFormLimit(this.pagination.totalItems)
    },

    hasFormsFeatureAccess() {
      return this.userMixin_hasFeatureAccess(featureSlugs.FORMS)
    },

    leadNotificationSetting: function () {
      return this.$store.getters['setting/leadNotificationSetting']
    },
    totalUnreadLeads() {
      return this.listData.forms.reduce((total, form) => total + (form.unread_leads || 0), 0)
    },

    headers() {
      return [
        { title: '#', key: 'serial_no', sortable: false, width: '5%', align: 'center' },
        { title: 'Form', key: 'title', sortable: true, width: '27%' },
        { title: 'Conversion Rate', key: 'conversion_rate', sortable: false },
        { title: 'Total Leads', key: 'form_total_leads_count', sortable: true, },
        { title: 'Completed', key: 'form_leads_count', sortable: true, },
        { title: 'Partial', key: 'form_leads_partial_count', sortable: true, },
        ...(this.leadNotificationSetting?.enabled ? [{
          title: ' ',
          key: 'notifications',
          sortable: false
        }] : []),
        { title: 'Created At', key: 'created_at', sortable: true },
        // { title: 'Updated At', key: 'updated_at', sortable: true },
        { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '7%' }
      ]
    },
  },
  methods: {
    loadLeadNotificationSettings: function () {
      this.$store.dispatch('setting/fetchLeadNotificationSetting')
        .then((response) => {
          let responseData = response?.data?.data
          this.$store.dispatch('setting/fetchLeadNotificationSettingSuccess', responseData)
        })
    },
    // Table operations
    loadItems({ page, itemsPerPage, sortBy }) {
      this.setLoading(true)

      // Update pagination if values provided
      if (page) this.pagination.currentPage = page
      if (itemsPerPage) this.pagination.itemsPerPage = itemsPerPage

      const params = this.buildRequestParams({ page, sortBy })
      this.fetchForms(params)
    },
    buildRequestParams({ page, sortBy }) {
      return {
        page: page || this.pagination.currentPage,
        per_page: this.pagination.itemsPerPage,
        search: {
          title: this.pagination.search || ''
        },
        sortField: sortBy?.length ? sortBy[0].key : 'id',
        sortDirection: sortBy?.length ? sortBy[0].order : 'desc',
        archived: false,
        resource: resourceTypes.FORMS_LIST
      }
    },
    // Data fetching
    async fetchForms(params) {
      try {
        const response = await this.$store.dispatch('formlists/fetchFormLists', params)
        this.updateFormsData(response?.data)
      } catch (err) {
        this.handleError(err)
      } finally {
        this.setLoading(false)
      }
    },
    // State management
    updateFormsData(data) {
      const shouldBlockAllForms = !this.hasFormsFeatureAccess && !this.isAdmin && !this.isSuperCustomer
      this.listData.forms = (data?.data || []).map(form => ({
        ...form,
        blocked: shouldBlockAllForms ? true : form.blocked,
        speedDial: false
      }))
      this.pagination.totalItems = data?.pagination?.total || 0
    },
    setLoading(status) {
      this.listData.loading = status
    },
    handleError(err) {
      this.listData.error = true
      this.listData.message = err?.response?.data?.message || 'An error occurred while loading forms'
    },
    openActionModal(action, form = null) {
      this.model.form = form
      this.model.action = action
      this.model.isOpenModal = true
      this.model.message = null
    },
    async confirmAction(action) {
      // Map of actions to their corresponding handler functions
      const actionHandlers = {
        archive: this.handleFormArchive,
        duplicate: this.handleFormDuplicate
      }

      try {
        this.model.loader = true
        const forms = this.model.form ? [this.model.form] : this.listData.selected

        // Execute the appropriate handler based on action
        const handler = actionHandlers[action]
        if (handler) {
          await handler(forms)
        }

        // Reset selection and close modal
        this.listData.selected = []
        this.model.isOpenModal = false
      } catch (error) {
        this.handleActionError(error)
      } finally {
        this.model.loader = false
      }
    },
    // Add this new method for snackbar handling
    showSnackbar({ text, error = false, timeout = 5000 }) {
      this.snackbar = {
        text,
        error,
        show: true,
        timeout
      }
    },
    /**
     * Handle form duplication - main entry point
     * Validates permissions, input data, and prepares form IDs
     * @param {Array} forms - Array of form objects or IDs to duplicate
     */
    async handleFormDuplicate(forms) {
      // Step 1: Permission validation - Check if user can create more forms
      if (!this.bulkDuplicateAllowed) {
        const isRestrictedByAccountState = [
          (this.userMixin_hasActiveFreeTrialPlan && (!this.userMixin_inTrial || this.userMixin_planExpired)),
          this.userMixin_hasActiveFreePlan,
          this.userMixin_isPastDueUser,
          (this.userMixin_hasPausedSubscription && !this.userMixin_hasPausedSubscriptionInGrace)
        ].some(Boolean)

        // Check specific reasons why permission might be denied
        if (isRestrictedByAccountState) {
          this.showSnackbar({
            text: 'You do not have permission to duplicate forms. Please check your account status.',
            error: true
          })
        } else if (this.formLimit <= this.pagination.totalItems) {
          this.showSnackbar({
            text: 'You have reached your form creation limit. Please upgrade your plan.',
            error: true
          })
        } else {
          this.showSnackbar({
            text: 'You do not have permission to duplicate forms.',
            error: true
          })
        }
        return
      }

      // Step 2: Determine operation type based on forms array length
      const isBulkOperation = forms?.length > 1 // True if multiple forms
      const formCount = forms?.length || 0      // Total number of forms

      // Step 3: Input validation - Ensure we have forms to process
      if (!forms || formCount === 0) {
        this.showSnackbar({ text: 'No forms selected for duplication', error: true })
        return
      }

      // Step 4: Check if user has enough form slots for duplication
      const formsToCreate = formCount
      const currentForms = this.pagination.totalItems
      const formLimit = this.formLimit || 0

      if (currentForms + formsToCreate > formLimit) {
        this.showSnackbar({
          text: `You can only create ${formLimit - currentForms} more form(s). Please upgrade your plan to duplicate ${formCount} form(s).`,
          error: true
        })
        return
      }

      // Step 5: Extract and validate form IDs based on operation type
      let formIds = []

      if (isBulkOperation) {
        // Bulk operation: Extract IDs from multiple forms
        // Handle both object format {id: 123} and direct ID format 123
        formIds = forms.map(form => form?.id || form).filter(id => id)

        // Validation: Ensure we found valid IDs
        if (formIds.length === 0) {
          this.showSnackbar({ text: 'No valid form IDs found for bulk duplication', error: true })
          return
        }
      } else {
        // Single operation: Extract ID from single form
        const formId = forms[0]?.id || forms[0]

        // Validation: Ensure the ID is valid
        if (!formId) {
          this.showSnackbar({ text: 'Invalid form ID for duplication', error: true })
          return
        }

        formIds = [formId] // Convert to array for consistent handling
      }

      // Step 6: Execute the duplication operation
      await this.handleOperationDuplicate(formIds, isBulkOperation, formCount)
    },
    /**
     * Handle duplicate operation execution and messaging
     * Executes API calls, manages loading state, and handles success/error scenarios
     * Uses unified endpoint for both single and bulk operations
     * @param {Array} formIds - Array of validated form IDs
     * @param {boolean} isBulkOperation - Whether this is a bulk operation
     * @param {number} formCount - Total number of forms being processed
     */
    async handleOperationDuplicate(formIds, isBulkOperation, formCount) {
      try {
        // Step 1: Start loading state
        this.model.loader = true

        // Step 2: Execute duplication API call using unified endpoint
        if (isBulkOperation) {
          // Bulk duplicate: Use unified endpoint with ids array
          const payload = { id: formIds[0], ids: formIds }
          await this.$store.dispatch(actionTypes.FORM_DUPLICATE, payload)
        } else {
          // Single duplicate: Use unified endpoint with single id
          const payload = { id: formIds[0] }
          await this.$store.dispatch(actionTypes.FORM_DUPLICATE, payload)
        }

        // Step 3: Update store state for successful duplication
        await this.$store.dispatch(actionTypes.FORM_DUPLICATE_SUCCESS)

        // Step 4: Show appropriate success message based on operation type
        const message = isBulkOperation
          ? `${formCount} forms duplicated successfully`  // Bulk message
          : 'Form duplicated successfully'                // Single message
        this.showSnackbar({ text: message, error: false })

        // Step 5: Reset UI state and refresh data
        this.model.isOpenModal = false    // Close confirmation modal
        this.listData.selected = []       // Clear selected items
        setTimeout(() => this.loadItems({}), 500) // Refresh list with delay

      } catch (error) {
        // Handle error scenarios
        // Extract meaningful error message from response
        const errorMessage = error?.response?.data?.meta?.error_message ||
          error?.response?.data?.message ||
          error?.message ||
          'An error occurred while duplicating the form(s). Please try again.'

        // Show error to user
        this.showSnackbar({ text: errorMessage, error: true })

        // Step 6: Update store error state
        await this.$store.dispatch(actionTypes.FORM_DUPLICATE_ERROR)
      } finally {
        // Always stop loading state regardless of success/error
        this.model.loader = false
      }
    },
    /**
     * Handle form archive - main entry point
     * Validates input data and prepares form IDs for archiving
     * @param {Array} forms - Array of form objects or IDs to archive
     */
    async handleFormArchive(forms) {
      // Step 1: Determine operation type based on forms array length
      const isBulkOperation = forms?.length > 1 // True if multiple forms
      const formCount = forms?.length || 0      // Total number of forms

      // Step 2: Input validation - Ensure we have forms to process
      if (!forms || formCount === 0) {
        this.showSnackbar({ text: 'No forms selected for archive', error: true })
        return
      }

      // Step 3: Extract and validate form IDs based on operation type
      let formIds = []

      if (isBulkOperation) {
        // Bulk operation: Extract IDs from multiple forms
        // Handle both object format {id: 123} and direct ID format 123
        formIds = forms.map(form => form?.id || form).filter(id => id)

        // Validation: Ensure we found valid IDs
        if (formIds.length === 0) {
          this.showSnackbar({ text: 'No valid form IDs found for bulk archive', error: true })
          return
        }
      } else {
        // Single operation: Extract ID from single form
        const formId = forms[0]?.id || forms[0]

        // Validation: Ensure the ID is valid
        if (!formId) {
          this.showSnackbar({ text: 'Invalid form ID for archive', error: true })
          return
        }

        formIds = [formId] // Convert to array for consistent handling
      }

      // Step 4: Execute the archive operation
      await this.handleOperationArchive(formIds, isBulkOperation, formCount)
    },

    /**
     * Handle archive operation execution and messaging
     * Executes API calls, manages loading state, and handles success/error scenarios
     * Uses unified endpoint for both single and bulk operations
     * @param {Array} formIds - Array of validated form IDs
     * @param {boolean} isBulkOperation - Whether this is a bulk operation
     * @param {number} formCount - Total number of forms being processed
     */
    async handleOperationArchive(formIds, isBulkOperation, formCount) {
      try {
        // Step 1: Start loading state
        this.model.loader = true

        // Step 2: Execute archive API call using unified endpoint
        if (isBulkOperation) {
          // Bulk archive: Use unified endpoint with ids array
          const payload = { id: formIds[0], ids: formIds }
          await this.$store.dispatch(actionTypes.FORM_ARCHIVE, payload)
        } else {
          // Single archive: Use unified endpoint with single id
          const payload = { id: formIds[0] }
          await this.$store.dispatch(actionTypes.FORM_ARCHIVE, payload)
        }

        // Step 3: Update store state for successful archive
        await this.$store.dispatch(actionTypes.FORM_ARCHIVE_SUCCESS)

        // Step 4: Show appropriate success message based on operation type
        const message = isBulkOperation
          ? `${formCount} forms archived successfully`  // Bulk message
          : 'Form archived successfully'                // Single message
        this.showSnackbar({ text: message, error: false })

        // Step 5: Reset UI state and refresh data
        this.model.isOpenModal = false    // Close confirmation modal
        this.listData.selected = []       // Clear selected items
        setTimeout(() => this.loadItems({}), 500) // Refresh list with delay

      } catch (error) {
        // Handle error scenarios
        // Extract meaningful error message from response
        const errorMessage = error?.response?.data?.meta?.error_message ||
          error?.response?.data?.message ||
          error?.message ||
          'An error occurred while archiving the form(s). Please try again.'

        // Show error to user
        this.showSnackbar({ text: errorMessage, error: true })

        // Step 6: Update store error state
        await this.$store.dispatch(actionTypes.FORM_ARCHIVE_ERROR)
      } finally {
        // Always stop loading state regardless of success/error
        this.model.loader = false
      }
    },
    /**
     * Handle action errors with contextual messaging
     * Generates appropriate error messages based on operation type and context
     * @param {Object} error - Error object from failed operation
     */
    handleActionError(error) {
      // Step 1: Generate contextual default message based on operation context
      const defaultMsg = this.model?.form
        ? `An error occurred while ${this.model?.action}ing the form "${this.model?.form?.title}"` // Single form operation
        : `An error occurred while ${this.model?.action}ing ${this.listData?.selected?.length} selected forms` // Bulk operation

      // Step 2: Extract error message with fallback hierarchy
      // Priority: API meta message > API message > Error message > Default contextual message
      const errorMessage = error?.response?.data?.meta?.error_message || // Server meta error (highest priority)
        error?.response?.data?.message ||                                 // Server response message
        error?.message ||                                                 // JavaScript error message
        defaultMsg                                                        // Generated contextual message (fallback)

      // Step 3: Display error message to user via snackbar
      this.showSnackbar({
        text: errorMessage,
        error: true // Mark as error for red styling
      })
    },
    /**
     * Handle notification bell click to mark leads as read
     * Processes unread leads across all forms and marks them as read
     * Uses legacy JSON format for backward compatibility with API
     */
    handleNotificationClick() {
      // Step 1: Early exit conditions - prevent unnecessary processing
      if (!this.totalUnreadLeads || this.notification.loader) return;

      // Step 2: Set loading state to prevent multiple simultaneous requests
      this.notification.loader = true;
      this.notification.viewed = false; // Reset viewed state

      try {
        // Step 3: Build form IDs array in legacy format for API compatibility
        // Note: Uses sparse array format where index = array position, value = form ID
        const jsonObj = [];
        Array.from(this.listData.forms).forEach((form, index) => {
          if (form.unread_leads > 0) {
            jsonObj[index] = form.id; // Set form ID at specific index (creates sparse array)
          }
        });

        // Step 4: Convert sparse array to JSON object format expected by API
        // Legacy API expects: {"0": 123, "2": 456} format, not standard array
        const formIds = JSON.stringify(Object.assign({}, jsonObj));

        // Step 5: Execute mark as read operation
        leadMixin.methods.markLeadAsViewed.call(this, formIds, true, false)

        // Success: Refresh the forms list to show updated lead counts
        this.loadItems({});
      } finally {
        // Always: Reset loading state regardless of success/failure
        this.notification.loader = false;
      }
    },
    formUrl(key) {
      return config.formsUrl + '/' + key
    },
    // Modal helper methods using maps for cleaner code
    getActionIcon(action) {
      // Map of actions to their corresponding icons
      const iconMap = new Map([
        ['archive', 'mdi-archive'],
        ['duplicate', 'mdi-content-copy']
      ])
      return iconMap.get(action) || 'mdi-alert'
    },

    getActionTitle(action) {
      // Map of actions to their display titles
      const titleMap = new Map([
        ['archive', 'Archive'],
        ['duplicate', 'Duplicate']
      ])
      return titleMap.get(action) || action
    },

    getAlertType(action) {
      // Map of actions to their alert types
      const alertTypeMap = new Map([
        ['archive', 'error'],
        ['duplicate', 'info']
      ])
      return alertTypeMap.get(action) || 'info'
    },

    getActionColor(action) {
      // Map of actions to their button colors
      const colorMap = new Map([
        ['archive', 'error'],
        ['duplicate', 'info']
      ])
      return colorMap.get(action) || 'error'
    },

    // Form creation dialog methods
    navigateToBlank() {
      this.createFormDialog = false
      this.$router.push({ name: 'forms.create' })
    },

    navigateToTemplate() {
      this.createFormDialog = false
      this.$router.push({ name: 'forms.choose.template' })
    },
  },
  watch: {
    'pagination.search': {
      handler() {
        this.pagination.currentPage = 1 // Reset to first page when searching
        this.loadItems({})
      }
    }
  },
  mounted() {
    this.loadItems({})
    this.loadLeadNotificationSettings()
  }
}
</script>

<style scoped lang="scss">
.form-option-card {
  border: 2px solid rgb(var(--v-theme-surface-variant));
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  height: 100%;
  overflow: hidden;

  &:hover {
    border-color: rgb(var(--brand-color-rgb));
    box-shadow: 0 8px 24px rgba(var(--brand-color-rgb), 0.15);
    transform: translateY(-4px);

    .form-option-preview {
      background: linear-gradient(135deg, rgba(var(--brand-color-rgb), 0.08) 0%, rgba(var(--brand-color-rgb), 0.03) 100%);
    }

    .template-card:first-child {
      transform: translateY(-4px);
    }

    .template-card.offset {
      transform: translateY(-2px);
    }
  }

  &:active {
    transform: translateY(-2px);
  }
}

// Wrapper with padding and border radius
.form-option-preview-wrapper {
  padding: 16px;
}

.form-option-preview {
  background: rgb(var(--v-theme-surface-light));
  border-radius: 12px;
  padding: 48px 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 220px;
  transition: all 0.3s ease;
}

.preview-content {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 280px;
}

// Blank form visual styles
.blank-form-visual {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  width: 100%;
}

.form-placeholder-line {
  height: 12px;
  background: rgb(var(--v-theme-surface-variant));
  border-radius: 6px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  &.short {
    width: 40%;
    margin-bottom: 20px;
  }

  &.medium {
    width: 60%;
  }
}

// Template visual styles
.template-visual {
  position: relative;
  width: 100%;
  height: 180px;
}

.template-card {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  position: absolute;
  width: 85%;
  transition: transform 0.3s ease;

  &:first-child {
    top: 0;
    left: 0;
    z-index: 2;
  }

  &.offset {
    top: 20px;
    left: 15%;
    z-index: 1;
    opacity: 0.6;
  }
}

.template-card-header {
  height: 8px;
  width: 50%;
  background: rgb(var(--v-theme-info));
  border-radius: 4px;
  margin-bottom: 12px;
}

.template-card-line {
  height: 8px;
  background: rgb(var(--v-theme-surface-variant));
  border-radius: 4px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }

  &.short {
    width: 70%;
  }
}

.template-badge {
  padding: 2px 8px;
  font-size: 10px;
}
</style>
